// SPEC_BLOCO6 (19/07/2026): Fila de Homologação Side-by-Side - motor propõe, humano homologa.
// Rota: /homologacao-enquadramento
// Ref: docs/estado/SPEC_BLOCO6_FILA-HOMOLOGACAO_15-07.md

import { useState, useEffect, useCallback } from 'react';
import { homologacaoEnquadramentoApi } from '../services/api';
import type { ItemFilaHomologacao, VeredictoHomologacao, FaixaEnquadramento } from '../types';

const FAIXAS: FaixaEnquadramento[] = ['FAIXA_1', 'FAIXA_2', 'FAIXA_3', 'CLASSE_MEDIA'];

function formatarData(iso?: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatarMoeda(valor: number | null): string {
  if (valor == null) return '-';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function badgeDecisao(decisao: string) {
  const cores: Record<string, string> = {
    FAIXA_1: 'bg-green-100 text-green-800',
    FAIXA_2: 'bg-green-100 text-green-800',
    FAIXA_3: 'bg-green-100 text-green-800',
    CLASSE_MEDIA: 'bg-blue-100 text-blue-800',
    PRO_COTISTA: 'bg-purple-100 text-purple-800',
    SBPE: 'bg-amber-100 text-amber-800',
    FORA_DE_ESCOPO: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cores[decisao] ?? 'bg-gray-100 text-gray-600'}`}>
      {decisao}
    </span>
  );
}

// ── Formulário de veredito (linha expandida) ─────────────────────────────────

function FormularioVeredito({
  item, onSalvar, salvando,
}: {
  item: ItemFilaHomologacao;
  onSalvar: (dados: { veredicto: VeredictoHomologacao; faixaAjustada?: FaixaEnquadramento; justificativa?: string }) => void;
  salvando: boolean;
}) {
  const [veredicto, setVeredicto] = useState<VeredictoHomologacao>('APROVA');
  const [faixaAjustada, setFaixaAjustada] = useState<FaixaEnquadramento>('FAIXA_1');
  const [justificativa, setJustificativa] = useState('');

  const precisaJustificativa = veredicto !== 'APROVA';
  const podeSalvar = !precisaJustificativa || justificativa.trim().length > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-3 text-sm">
        {(['APROVA', 'AJUSTA', 'REJEITA'] as VeredictoHomologacao[]).map((v) => (
          <label key={v} className="flex items-center gap-1 cursor-pointer">
            <input type="radio" name={`veredicto-${item.id}`} checked={veredicto === v} onChange={() => setVeredicto(v)} />
            {v === 'APROVA' ? 'Aprovar' : v === 'AJUSTA' ? 'Ajustar' : 'Rejeitar'}
          </label>
        ))}
      </div>

      {veredicto === 'AJUSTA' && (
        <select
          value={faixaAjustada}
          onChange={(e) => setFaixaAjustada(e.target.value as FaixaEnquadramento)}
          className="text-sm border border-gray-300 rounded px-2 py-1.5 w-fit"
        >
          {FAIXAS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      )}

      {precisaJustificativa && (
        <textarea
          value={justificativa}
          onChange={(e) => setJustificativa(e.target.value)}
          placeholder="Justificativa (obrigatória para ajustar/rejeitar)"
          className="text-sm border border-gray-300 rounded px-2 py-1.5 w-full"
          rows={2}
        />
      )}

      <button
        onClick={() => onSalvar({ veredicto, faixaAjustada: veredicto === 'AJUSTA' ? faixaAjustada : undefined, justificativa: justificativa.trim() || undefined })}
        disabled={!podeSalvar || salvando}
        className="text-xs bg-otm-orange text-white px-3 py-1.5 rounded hover:bg-otm-orange-dark disabled:opacity-50 w-fit"
      >
        {salvando ? 'Salvando…' : 'Registrar veredito'}
      </button>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function HomologacaoEnquadramento() {
  const [items, setItems] = useState<ItemFilaHomologacao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<'PENDENTE' | 'DECIDIDO'>('PENDENTE');
  const [salvando, setSalvando] = useState<Record<string, boolean>>({});

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const data = await homologacaoEnquadramentoApi.listar(filtroStatus);
      setItems(data);
    } catch {
      setErro('Erro ao carregar a fila de homologação. Verifique a conexão.');
    } finally {
      setCarregando(false);
    }
  }, [filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  async function salvarVeredito(id: string, dados: { veredicto: VeredictoHomologacao; faixaAjustada?: FaixaEnquadramento; justificativa?: string }) {
    setSalvando((s) => ({ ...s, [id]: true }));
    try {
      await homologacaoEnquadramentoApi.registrarVeredito(id, dados);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e: any) {
      alert(`Erro ao registrar veredito: ${e?.response?.data?.erro ?? e.message}`);
    } finally {
      setSalvando((s) => { const c = { ...s }; delete c[id]; return c; });
    }
  }

  return (
    <div className="min-h-full">
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <h1 className="text-lg font-semibold text-gray-900">Fila de Homologação - Motor de Enquadramento PMCMV</h1>
        <p className="text-sm text-gray-500 mt-0.5">{items.length} item{items.length !== 1 ? 'ns' : ''} {filtroStatus === 'PENDENTE' ? 'pendente(s)' : 'decidido(s)'}</p>
      </header>

      <div className="bg-white border-b border-gray-200 px-6 py-3 flex gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value as 'PENDENTE' | 'DECIDIDO')}
            className="text-sm border border-gray-300 rounded px-2 py-1.5"
          >
            <option value="PENDENTE">Pendentes</option>
            <option value="DECIDIDO">Decididos</option>
          </select>
        </div>
        <button onClick={carregar} className="text-sm bg-otm-orange text-white px-3 py-1.5 rounded hover:bg-otm-orange-dark">
          Filtrar
        </button>
      </div>

      <main className="px-6 py-4 flex flex-col gap-4">
        {carregando && <p className="text-sm text-gray-500 py-8 text-center">Carregando…</p>}
        {!carregando && erro && <p className="text-sm text-red-600 py-8 text-center">{erro}</p>}
        {!carregando && !erro && items.length === 0 && (
          <p className="text-sm text-gray-500 py-8 text-center">Nenhum item na fila para este filtro.</p>
        )}

        {!carregando && !erro && items.map((item) => (
          <div key={item.id} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between text-sm">
              <span className="font-medium text-gray-900">
                Operação {item.operacaoNumero}
                {badgeDecisao(item.saida.decisaoMotor)}
              </span>
              <span className="text-xs text-gray-500" title="Rodadas criadas por sync automático (auditoria do gatilho, veredito V6)">
                Rodadas por sync automático: {item.contadorRodadasSyncAutomatico}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">
              {/* Entrada */}
              <div className="p-4 text-sm">
                <h3 className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-2">Entrada</h3>
                <dl className="space-y-1">
                  <div className="flex justify-between"><dt className="text-gray-500">Renda familiar</dt><dd>{formatarMoeda(item.entrada.rendaFamiliarBruta)}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Município</dt><dd>{item.entrada.cidade ?? '-'}/{item.entrada.estado ?? '-'}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Valor do imóvel</dt><dd>{formatarMoeda(item.entrada.valorImovel)}</dd></div>
                </dl>
              </div>

              {/* Saída do motor */}
              <div className="p-4 text-sm">
                <h3 className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-2">Saída do motor</h3>
                <dl className="space-y-1 mb-2">
                  <div className="flex justify-between"><dt className="text-gray-500">Decisão</dt><dd>{item.saida.decisaoMotor}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Faixa PMCMV</dt><dd>{item.saida.faixaEnquadramento ?? '-'}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Data de referência</dt><dd>{formatarData(item.saida.dataReferencia)}</dd></div>
                </dl>
                {item.saida.motivos.length > 0 && (
                  <ul className="text-xs text-gray-500 list-disc list-inside space-y-0.5">
                    {item.saida.motivos.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                )}
              </div>

              {/* Veredito */}
              <div className="p-4">
                <h3 className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-2">Veredito</h3>
                {item.veredicto ? (
                  <div className="text-sm">
                    <p><span className="font-medium">{item.veredicto}</span> {item.faixaAjustada ? `→ ${item.faixaAjustada}` : ''}</p>
                    {item.justificativa && <p className="text-gray-500 mt-1">{item.justificativa}</p>}
                    <p className="text-xs text-gray-400 mt-2">{item.autor?.nome} · {formatarData(item.decididoEm)}</p>
                  </div>
                ) : (
                  <FormularioVeredito item={item} salvando={!!salvando[item.id]} onSalvar={(dados) => salvarVeredito(item.id, dados)} />
                )}
              </div>
            </div>
          </div>
        ))}
      </main>
    </div>
  );
}
